import * as Sentry from '@sentry/nextjs';

import { isCMSV3 } from '@/helpers/conf';
import { CLIENT_ENVS } from '@/helpers/constants';

export interface CMSApiBase {
  id: number;
  locale: string;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
  localizations: any[];
}

type CMSImageFormatDetail = {
  name: string;
  hash: string;
  ext: string;
  mime: string;
  width: number;
  height: number;
  size: number;
  path: string | null;
  url: string;
};

interface CMSImageFormats {
  thumbnail: CMSImageFormatDetail;
  large: CMSImageFormatDetail;
  medium: CMSImageFormatDetail;
  small: CMSImageFormatDetail;
}

export interface CMSImageBase {
  id: number;
  name: string;
  alternativeText: string;
  caption: string;
  width: number;
  height: number;
  formats: CMSImageFormats | null;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: string | null;
  provider: string;
  providerMetadata: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CMSResponsiveImageComponent {
  image_1394: CMSImageBase;
  image_1088: CMSImageBase;
  image_936: CMSImageBase;
  image_622: CMSImageBase;
  image_452: CMSImageBase;
}

export type CMSCountryParams = {
  countryCodesInclude: string | null;
  countryCodesExclude: string | null;
};

export interface CMSArrayBase {
  id: number;
}

const correctLang = (lang: string) => lang.replace('no', 'nn');

function getFormatedParams(
  path: string,
  params: Record<string, string | number | undefined> = {}
) {
  const queryParams = new URLSearchParams();

  for (const key in params) {
    const value = params[key];
    if (value !== undefined && value !== null) {
      queryParams.append(key, value.toString());
    }
  }

  return queryParams.toString();
}

const DEFAULT_CMS_PAGE_SIZE = 100;

export const buildCmsUrl = ({
  path,
  lang,
  limit,
  additionalParams,
}: {
  path: string;
  lang?: string;
  limit?: number;
  additionalParams?: Record<string, string | number | undefined>;
}) => {
  try {
    Sentry.addBreadcrumb({
      category: 'CMS',
      message: `Building CMS URL`,
      level: 'info',
      data: {
        path,
        lang,
        limit,
        additionalParams,
      },
    });

    const cmsParams = isCMSV3
      ? {
          _locale: lang ? correctLang(lang) : undefined,
          _limit: limit,
        }
      : {
          locale: lang ? correctLang(lang) : undefined,
          'pagination[pageSize]': limit || DEFAULT_CMS_PAGE_SIZE,
        };

    const formatedParams = getFormatedParams(path, {
      ...cmsParams,
      ...additionalParams,
    });

    return `${CLIENT_ENVS.CMS_URL}/${path}?${formatedParams}`;
  } catch (error) {
    Sentry.withScope((scope) => {
      Sentry.setTags({
        category: 'CMS',
        pathToFile: 'src/api/cms/base.ts',
      });

      scope.setLevel('error');
      Sentry.captureMessage('Error while building cms url');
    });
    console.log(error);
    return '';
  }
};

export enum CMSGeneralButtonType {
  DEPOSIT = 'deposit',
  SHOW_MORE = 'show_more',
  EXPLORE = 'explore',
  READ_MORE = 'read_more',
  JOIN_NOW = 'join_now',
  PLAY_NOW = 'play_now',
}

export enum CMSGroupMode {
  AND = 'AND',
  OR = 'OR',
}

export type CMSGroupParams = {
  showGuestToggle: boolean;

  groupsIncludeMode: CMSGroupMode;
  groupsInclude: string | null;
  groupsExcludeMode: CMSGroupMode;
  groupsExclude: string | null;

  durationFrom: string | null;
  durationTo: string | null;
};

export interface CMSOrder {
  order?: number;
}

export interface CMSToggle {
  cashierToggle: boolean;
  promoPageToggle: boolean;
  termsAndConditionsToggle: boolean;
  countdownToggle: boolean;
}

export enum EWeekday {
  Sunday = 'Sunday',
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
}

export type CMSWeekday = { weekday: EWeekday };

export type CMSScheduler = {
  scheduler?: CMSWeekday[];
};

export const LIMIT_500 = 500;

export const getResponseCMS = <T>(data: { data: T }) => data.data;

export interface CMSFaqItem {
  question: string;
  answer: string;
}

export interface CMSFaq extends CMSGroupParams {
  items: CMSFaqItem[];
}

export interface CMSHowToParticipateCard {
  id: number;
  title: string;
  description: string;
  cardImage: CMSImageBase;
}

export interface CMSHowToParticipate extends CMSGroupParams {
  cards: CMSHowToParticipateCard[];
}
